import React, { useEffect, useRef } from "react"
import Globe from "globe.gl"
import { useStaticQuery, graphql, withPrefix } from "gatsby"
import Stream, { READY_STATES } from "~utils/stream"
import isClientSide from "~utils/is-client-side"
import { distInKm, ResumableTimer } from "./utils.js"

import {
  SOURCE_URL,
  MAX_HISTORY,
  ARC_FLIGHT_TIME,
  ARC_HOLD,
  MIN_ARC_DIST,
  MAX_ARC_DIST,
  MAX_START_FROM_POV,
  HIGHLIGHT_MAX_START_FROM_POV,
  HIGHLIGHT_MIN_ARC_DIST,
  HIGHLIGHT_CONTENT,
  CLEANUP_INTERVAL,
  SPIN_INTERVAL,
  POVS,
  SERVICES,
  SERVICE_COLORS,
  HIGHLIGHT_COLOR,
} from "./config.js"
import * as styles from "./globe-traffic-animation.module.css"

const GQL = graphql`
  query {
    pops: allPopsJson(sort: { fields: name }) {
      nodes {
        popCode
        name
        pnis
        shieldCode
        recommend
        hostCount
        geo {
          lat
          lng
        }
      }
    }
  }
`

const createGlobe = (el, pxHeight) => {
  const g = Globe({ animateIn: false })(el)
    .globeImageUrl(withPrefix("/earth-night.jpg"))
    .backgroundColor("rgb(0,0,0)")
    .width(pxHeight)
    .height(pxHeight)
    .pointOfView({ lat: 35, lng: -98.5, altitude: 1.6 })
    .lineHoverPrecision(5)

    .arcDashLength(ARC_HOLD)
    .arcDashGap(ARC_HOLD + 1)
    .arcDashInitialGap(1)
    .arcDashAnimateTime(ARC_FLIGHT_TIME)
    .arcColor((req) => (req.isHighlighted ? HIGHLIGHT_COLOR : SERVICE_COLORS[req.service] || "white"))
    .arcStroke((req) => (req.isHighlighted ? 0.75 : 0.4))
    .arcsTransitionDuration(0)

    .labelLat((p) => p.geo.lat)
    .labelLng((p) => p.geo.lng)
    .labelText((p) => p.name)
    .labelSize(0.5)
    .labelDotRadius(0.2)
    .labelDotOrientation(() => "right")
    .labelColor(() => "rgba(255, 255, 255, 0.75)")
    .labelResolution(2)

  g.controls().enableZoom = false
  return g
}

const getUserPOP = () => {
  const nt = window.performance.getEntriesByType("navigation")[0]
  if (!nt || !nt.serverTiming) return null
  const raw = nt.serverTiming.find((x) => x.name === "fastly")
  if (!raw) return null
  const data = raw.description.split(":")
  return data[0]
}

const pauseFor = async (dur) => new Promise((resolve) => setTimeout(resolve, dur))

const Component = ({ height }) => {
  const globeEl = useRef()
  const labelEl = useRef()
  const pops = useStaticQuery(GQL).pops.nodes

  useEffect(() => {
    let cleanupTimer, spinTimer, myGlobe, stream
    let states = { intersect: false, docvis: true }
    let highlightNextArc = true
    const visiblePOPs = []

    const init = () => {
      // Set initial position to be near the viewer
      const userPOP = getUserPOP() || "LON"
      const userPos = userPOP && (pops.find((p) => p.popCode === userPOP) || {}).geo
      const initialPovIdx = userPos
        ? POVS.map(({ lat, lng }, idx) => ({ idx, dist: distInKm(lat, lng, userPos.lat, userPos.lng) }))
            .sort((a, b) => (a.dist < b.dist ? 1 : -1))
            .pop().idx
        : 0
      myGlobe = createGlobe(globeEl.current, globeEl.current.offsetHeight)
      myGlobe.pointOfView(POVS[initialPovIdx])

      stream = new Stream(SOURCE_URL)
      stream.on("stateChange", (newState) => {
        stream.off("traffic-log")
        if (newState === READY_STATES.OPEN) {
          setTimeout(() => {
            stream.on("traffic-log", newDataHandler)
          }, 500)
        } else {
          stream.off("traffic-log")
        }
      })
      stream.connect()
      cleanupTimer = new ResumableTimer(cleanup, CLEANUP_INTERVAL)
      spinTimer = new ResumableTimer(spin, SPIN_INTERVAL)
    }

    const stateChange = (newStates) => {
      states = { ...states, ...newStates }
      if ("docvis" in newStates && myGlobe) myGlobe.arcsData([])
      if (states.docvis && states.intersect) {
        if (states.intersect && !myGlobe) init()
        if (myGlobe) {
          myGlobe.resumeAnimation()
          cleanupTimer.resume()
          spinTimer.resume()
        }
      } else if (myGlobe) {
        myGlobe.pauseAnimation()
        cleanupTimer.pause()
        spinTimer.pause()
      }
    }

    const handleIntersect = (entries) => stateChange({ intersect: entries[0].isIntersecting })
    const handleVisibilityChange = () => stateChange({ docvis: document.visibilityState === "visible" })

    // When new data is received, draw new arcs
    const newDataHandler = (reqDataStr) => {
      const parts = reqDataStr.split(/\s+/)

      let service, popCode, latlng, elapsedUS, rtt, lang, cache, httpVer, tlsVer, browser, isMobile
      if (parts.length === 11) {
        ;[service, popCode, latlng, elapsedUS, rtt, lang, cache, httpVer, tlsVer, browser, isMobile] = parts
      } else if (parts.length === 9) {
        ;[service, popCode, latlng, elapsedUS, lang, cache, httpVer, tlsVer, browser] = parts
      }

      const pop = pops.find((p) => p.popCode === popCode)
      const clientGeo = (latlng || "").split(",").map(parseFloat)

      // Ignore incomplete events
      if (!service || !SERVICES[service] || !pop || !clientGeo || !myGlobe) return

      // Ignore arcs that start on the opposite side of the world to the current POV as they cannot be seen.
      // Circumference of the Earth is 40,000km so the furthest possible distance is 20,000km.
      const pov = myGlobe.pointOfView()
      const distFromPOV = distInKm(pop.geo.lat, pop.geo.lng, pov.lat, pov.lng)
      if (distFromPOV > MAX_START_FROM_POV) return

      // Define a new arc
      const newArc = {
        time: Date.now(),
        service,
        startLat: pop.geo.lat,
        startLng: pop.geo.lng,
        endLat: clientGeo[0],
        endLng: clientGeo[1],
        dist: Math.round(distInKm(pop.geo.lat, pop.geo.lng, clientGeo[0], clientGeo[1])),
        pop,
        elapsedMS: Number.parseInt(elapsedUS) / 1000 || null,
        rttMS: Number.parseInt(rtt) / 1000 || null,
        lang,
        cache,
        httpVer: Number.parseFloat(httpVer) || null,
        tlsVer: Number.parseFloat(tlsVer) || null,
        browser,
        isMobile: isMobile === "M",
        isHighlighted: false,
      }

      // Ignore arcs that are too short to see or stupidly long
      if (newArc.dist < MIN_ARC_DIST || newArc.dist > MAX_ARC_DIST) return

      // Don't paint arcs that start on the opposite side of the world to the current POV as they cannot be seen.
      // Circumference of the Earth is 40,000km so the furthest possible distance from any point is 20,000km, and
      // the distance to the horizon in either direction is 10,000km.  Call it 12,000.
      const currentPOV = myGlobe.pointOfView()
      if (distInKm(newArc.startLat, newArc.startLng, currentPOV.lat, currentPOV.lng) > 12000) return

      // Ignore arcs that cover a distance impossible to reach in the measured RTT
      if (newArc.rttMS) {
        const speedOfLightKmPerMS = 300
        const maxFeasibleDist = (newArc.rttMS * speedOfLightKmPerMS) / 2 // Need to go there and back so distance is halved
        if (newArc.dist > maxFeasibleDist) return
      }

      // Only highlight an arc if it's very close to the middle of the POV and long enough
      if (highlightNextArc && distFromPOV < HIGHLIGHT_MAX_START_FROM_POV && newArc.dist > HIGHLIGHT_MIN_ARC_DIST) {
        newArc.isHighlighted = true
        highlightNextArc = false
      }

      // Add the arc and schedule for cleanup after it has animated
      if (myGlobe.arcsData().length < MAX_HISTORY) {
        myGlobe.arcsData([...myGlobe.arcsData(), newArc])

        // Display a label for the POP if it's not already on the map
        if (!visiblePOPs.some((p) => p.name === newArc.pop.name)) {
          visiblePOPs.push(pops.find((p) => p.popCode === newArc.pop.popCode))
          myGlobe.labelsData(visiblePOPs)
        }

        if (newArc.isHighlighted) {
          const content = HIGHLIGHT_CONTENT(newArc)
          const posH = newArc.startLng < pov.lng ? "right" : "left"
          const posV = newArc.startLat < pov.lat ? "top" : "bottom"
          labelEl.current.innerHTML = content
          labelEl.current.className = [styles.mapLabel, styles[posH], styles[posV], styles.visible].join(" ")
          pauseFor((ARC_HOLD + 2) * ARC_FLIGHT_TIME).then(() => labelEl.current.classList.remove(styles.visible))
        }
      }
    }

    // Remove arcs that have completed their transition
    const cleanup = () => {
      if (!myGlobe) return
      const now = Date.now()
      const arcLifetime = ARC_FLIGHT_TIME * (ARC_HOLD * 2)
      const updatedArcsList = myGlobe.arcsData().filter((a) => a.time + arcLifetime > now)
      myGlobe.arcsData(updatedArcsList)
    }

    // Move to a new point of view and highlight a request
    const spin = async () => {
      if (!myGlobe) return
      labelEl.current.replaceChildren()
      const pos = myGlobe.pointOfView()
      const nextPov = POVS.find((p) => p.lng > pos.lng) || POVS[0]
      myGlobe.pointOfView(nextPov, 1000)
      setTimeout(() => {
        highlightNextArc = true
      }, 1000)
    }

    document.addEventListener("visibilitychange", handleVisibilityChange)
    const iObs = new IntersectionObserver(handleIntersect)
    iObs.observe(globeEl.current)

    return () => {
      if (stream) stream.close()
      if (cleanupTimer) cleanupTimer.pause()
      if (spinTimer) spinTimer.pause()
      document.removeEventListener("visibilitychange", handleVisibilityChange)
      iObs.disconnect()
      myGlobe = null
    }
  }, [pops])

  if (!isClientSide()) return null

  return (
    <>
      <div className={styles.fill} ref={globeEl} />
      <div className={`${styles.fill} ${styles.noPointer}`}>
        <div className={styles.mapLabel} ref={labelEl}></div>
      </div>
    </>
  )
}

export default Component
